import React, { useState } from 'react';
import { Typography } from '@mui/material';
import { integrationApiService, IntegrationConfig, IntegrationSettings } from '../services/integrationApi';
import { useOrgFeatureFlag } from '../hooks/useOrgFeatureFlags';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { useToast } from '../contexts/ToastContext';
import { tokens } from '../theme/tokens';
import { SectionCard } from './ui';
import { OwnerChoice } from './ShopifyCategoryMappingSection';

export interface BigCommerceCategoryOwnerCardProps {
  integration: IntegrationConfig;
  /** Refetch the integration after a change. */
  onChanged: () => void;
}

/**
 * Jordan's "Product categories" owner choice for a BigCommerce integration (flag
 * `bigcommerce_category_rules`), the same three-way control as the Shopify field owners. Hidden
 * entirely while the flag is off; a missing value shows as Motzu owns, what the sync does until chosen.
 */
const BigCommerceCategoryOwnerCard: React.FC<BigCommerceCategoryOwnerCardProps> = ({ integration, onChanged }) => {
  const { enabled } = useOrgFeatureFlag('bigcommerce_category_rules');
  const { t } = useAppTranslation();
  const { showToast } = useToast();
  const [busy, setBusy] = useState(false);

  if (!enabled || integration.platform !== 'bigcommerce') return null;

  const save = async (owner: NonNullable<IntegrationSettings['bigcommerceCategoryOwner']>) => {
    if (busy) return;
    setBusy(true);
    try {
      // The server merges settings, so only the one key is sent.
      await integrationApiService.updateIntegration(integration.id, { settings: { bigcommerceCategoryOwner: owner } as IntegrationConfig['settings'] });
      onChanged();
    } catch (error) {
      showToast(error instanceof Error && error.message ? error.message : t('integrations.bigcommerceCategories.saveFailed'), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <SectionCard title={t('integrations.bigcommerceCategories.title')} subtitle={t('integrations.bigcommerceCategories.subtitle')}>
      <OwnerChoice
        label={t('integrations.bigcommerceCategories.ownerLabel')}
        value={integration.settings?.bigcommerceCategoryOwner}
        onChange={save}
      />
      <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted }}>{t('integrations.bigcommerceCategories.help')}</Typography>
    </SectionCard>
  );
};

export default BigCommerceCategoryOwnerCard;
